import { cn } from '@/shared/helpers/classnames'
import {
  extractResponsiveInitialValue,
  extractResponsiveValue,
} from '@/shared/ui/layouts/responsive-utils'

import { tv } from 'tailwind-variants'

import { GridCell } from './GridCell'
import { GridProps } from './types'

/**
 * Composant de grille principal avec support du Compound Pattern.
 * Crée une grille CSS flexible avec support responsive complet.
 *
 * @example
 * // Grille simple avec 3 colonnes
 * <Grid columns="3" gap="4">
 *   <Grid.Cell>Élément 1</Grid.Cell>
 *   <Grid.Cell>Élément 2</Grid.Cell>
 *   <Grid.Cell>Élément 3</Grid.Cell>
 * </Grid>
 *
 * @example
 * // Grille responsive avec colspan
 * <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="4">
 *   <Grid.Cell colSpan="2">Élément large</Grid.Cell>
 *   <Grid.Cell>Élément normal</Grid.Cell>
 * </Grid>
 *
 * ### Breakpoints disponibles
 * - `initial` : Valeur par défaut (tous les écrans)
 * - `sm` : Small (<= 640px) - applique `sm:` en max-width
 * - `md` : Medium (<= 768px) - applique `md:` en max-width
 * - `lg` : Large (<= 1024px) - applique `lg:` en max-width
 * - `xl` : Extra Large (<= 1280px) - applique `xl:` en max-width
 *
 * **Note:** Les breakpoints utilisent des requêtes max-width (max-sm:, max-md:, max-lg:, max-xl:)
 * ce qui signifie qu'un breakpoint s'applique jusqu'à cette largeur, pas à partir de cette largeur.
 *
 * @param columns Nombre de colonnes (1-12) ou objet responsive { initial, sm, md, lg, xl }
 * @param gap Espacement entre les éléments (0-10) ou objet responsive { initial, sm, md, lg, xl }
 * @param children Contenu de la grille (généralement des Grid.Cell)
 */
const GridRoot = ({ children, columns, gap, className }: GridProps) => {
  const columnsInitialValue = extractResponsiveInitialValue(columns)
  const gapInitialValue = extractResponsiveInitialValue(gap)

  const columnsSm = extractResponsiveValue('sm', columns)
  const columnsMd = extractResponsiveValue('md', columns)
  const columnsLg = extractResponsiveValue('lg', columns)
  const columnsXl = extractResponsiveValue('xl', columns)

  const gapSm = extractResponsiveValue('sm', gap)
  const gapMd = extractResponsiveValue('md', gap)
  const gapLg = extractResponsiveValue('lg', gap)
  const gapXl = extractResponsiveValue('xl', gap)

  const baseClasses = gridVariants({
    columns: columnsInitialValue,
    gap: gapInitialValue,
    columnsSm,
    columnsMd,
    columnsLg,
    columnsXl,
    gapSm,
    gapMd,
    gapLg,
    gapXl,
  })

  return <div className={cn(baseClasses, className)}>{children}</div>
}

GridRoot.displayName = 'Grid'

/**
 * Composant Grid avec sous-composant Cell pour des layouts complexes.
 */
export const Grid = Object.assign(GridRoot, {
  Cell: GridCell,
})

/**
 * Variants Tailwind pour la grille avec support responsive
 */
const gridVariants = tv({
  base: 'grid',
  variants: {
    columns: {
      '1': 'grid-cols-1',
      '2': 'grid-cols-2',
      '3': 'grid-cols-3',
      '4': 'grid-cols-4',
      '5': 'grid-cols-5',
      '6': 'grid-cols-6',
      '7': 'grid-cols-7',
      '8': 'grid-cols-8',
      '9': 'grid-cols-9',
      '10': 'grid-cols-10',
      '11': 'grid-cols-11',
      '12': 'grid-cols-12',
    },
    gap: {
      '0': 'gap-0',
      '1': 'gap-1',
      '2': 'gap-2',
      '3': 'gap-3',
      '4': 'gap-4',
      '5': 'gap-5',
      '6': 'gap-6',
      '7': 'gap-7',
      '8': 'gap-8',
      '9': 'gap-9',
      '10': 'gap-10',
    },
    // Variants responsives pour les colonnes
    columnsSm: {
      '1': 'max-sm:grid-cols-1',
      '2': 'max-sm:grid-cols-2',
      '3': 'max-sm:grid-cols-3',
      '4': 'max-sm:grid-cols-4',
      '5': 'max-sm:grid-cols-5',
      '6': 'max-sm:grid-cols-6',
      '7': 'max-sm:grid-cols-7',
      '8': 'max-sm:grid-cols-8',
      '9': 'max-sm:grid-cols-9',
      '10': 'max-sm:grid-cols-10',
      '11': 'max-sm:grid-cols-11',
      '12': 'max-sm:grid-cols-12',
    },
    columnsMd: {
      '1': 'max-md:grid-cols-1',
      '2': 'max-md:grid-cols-2',
      '3': 'max-md:grid-cols-3',
      '4': 'max-md:grid-cols-4',
      '5': 'max-md:grid-cols-5',
      '6': 'max-md:grid-cols-6',
      '7': 'max-md:grid-cols-7',
      '8': 'max-md:grid-cols-8',
      '9': 'max-md:grid-cols-9',
      '10': 'max-md:grid-cols-10',
      '11': 'max-md:grid-cols-11',
      '12': 'max-md:grid-cols-12',
    },
    columnsLg: {
      '1': 'max-lg:grid-cols-1',
      '2': 'max-lg:grid-cols-2',
      '3': 'max-lg:grid-cols-3',
      '4': 'max-lg:grid-cols-4',
      '5': 'max-lg:grid-cols-5',
      '6': 'max-lg:grid-cols-6',
      '7': 'max-lg:grid-cols-7',
      '8': 'max-lg:grid-cols-8',
      '9': 'max-lg:grid-cols-9',
      '10': 'max-lg:grid-cols-10',
      '11': 'max-lg:grid-cols-11',
      '12': 'max-lg:grid-cols-12',
    },
    columnsXl: {
      '1': 'max-xl:grid-cols-1',
      '2': 'max-xl:grid-cols-2',
      '3': 'max-xl:grid-cols-3',
      '4': 'max-xl:grid-cols-4',
      '5': 'max-xl:grid-cols-5',
      '6': 'max-xl:grid-cols-6',
      '7': 'max-xl:grid-cols-7',
      '8': 'max-xl:grid-cols-8',
      '9': 'max-xl:grid-cols-9',
      '10': 'max-xl:grid-cols-10',
      '11': 'max-xl:grid-cols-11',
      '12': 'max-xl:grid-cols-12',
    },
    // Variants responsives pour le gap
    gapSm: {
      '0': 'max-sm:gap-0',
      '1': 'max-sm:gap-1',
      '2': 'max-sm:gap-2',
      '3': 'max-sm:gap-3',
      '4': 'max-sm:gap-4',
      '5': 'max-sm:gap-5',
      '6': 'max-sm:gap-6',
      '7': 'max-sm:gap-7',
      '8': 'max-sm:gap-8',
      '9': 'max-sm:gap-9',
      '10': 'max-sm:gap-10',
    },
    gapMd: {
      '0': 'max-md:gap-0',
      '1': 'max-md:gap-1',
      '2': 'max-md:gap-2',
      '3': 'max-md:gap-3',
      '4': 'max-md:gap-4',
      '5': 'max-md:gap-5',
      '6': 'max-md:gap-6',
      '7': 'max-md:gap-7',
      '8': 'max-md:gap-8',
      '9': 'max-md:gap-9',
      '10': 'max-md:gap-10',
    },
    gapLg: {
      '0': 'max-lg:gap-0',
      '1': 'max-lg:gap-1',
      '2': 'max-lg:gap-2',
      '3': 'max-lg:gap-3',
      '4': 'max-lg:gap-4',
      '5': 'max-lg:gap-5',
      '6': 'max-lg:gap-6',
      '7': 'max-lg:gap-7',
      '8': 'max-lg:gap-8',
      '9': 'max-lg:gap-9',
      '10': 'max-lg:gap-10',
    },
    gapXl: {
      '0': 'max-xl:gap-0',
      '1': 'max-xl:gap-1',
      '2': 'max-xl:gap-2',
      '3': 'max-xl:gap-3',
      '4': 'max-xl:gap-4',
      '5': 'max-xl:gap-5',
      '6': 'max-xl:gap-6',
      '7': 'max-xl:gap-7',
      '8': 'max-xl:gap-8',
      '9': 'max-xl:gap-9',
      '10': 'max-xl:gap-10',
    },
  },
  defaultVariants: {
    columns: '1',
    gap: '0',
  },
})
